import {
  Award,
  Banknote,
  Bell,
  Briefcase,
  Hourglass,
  CalendarClock,
  CheckCircle2,
  FileCheck2,
  FileX2,
  Gavel,
  Handshake,
  Home,
  MessageSquare,
  RefreshCcw,
  ShieldCheck,
  ShieldX,
  Tag,
  Trophy,
  UserPlus,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { NotificationType, type NotificationDto } from "../../api/types/notification.types";
import { formatCurrency } from "../../components/ui/format";

export type NotificationTone = "brand" | "success" | "danger" | "info" | "warning" | "violet" | "neutral";

export interface NotificationMeta {
  Icon: LucideIcon;
  tone: NotificationTone;
  /** Short category label shown as a pill */
  category: string;
}

/**
 * Icon tile colors. Type is already carried by the icon and the title, so tiles stay
 * neutral; only outcomes the user must notice (approved / rejected) keep a status color.
 */
const NEUTRAL_TILE = "bg-gray-50 text-gray-500 ring-gray-200";
export const notificationToneClasses: Record<NotificationTone, string> = {
  brand: NEUTRAL_TILE,
  success: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  danger: "bg-red-50 text-red-600 ring-red-100",
  info: NEUTRAL_TILE,
  warning: NEUTRAL_TILE,
  violet: NEUTRAL_TILE,
  neutral: NEUTRAL_TILE,
};

/** Lucide icon, tone and category for every notification type (replaces the emoji map) */
export const getNotificationMeta = (type: string): NotificationMeta => {
  switch (type) {
    case NotificationType.BUY_BOX_MATCH:
      return { Icon: Home, tone: "brand", category: "Buy box match" };
    case NotificationType.NEW_BID:
      return { Icon: Gavel, tone: "warning", category: "New bid" };
    case NotificationType.BID_COUNTERED:
      return { Icon: RefreshCcw, tone: "warning", category: "Counter offer" };
    case NotificationType.BID_ACCEPTED:
    case NotificationType.BID_STATUS_CHANGE:
      return { Icon: CheckCircle2, tone: "success", category: "Bid status" };
    case NotificationType.BID_WON:
      return { Icon: Trophy, tone: "success", category: "Bid status" };
    case NotificationType.BID_DECLINED:
      return { Icon: XCircle, tone: "danger", category: "Bid status" };
    case NotificationType.BID_BACKUP:
      return { Icon: Gavel, tone: "info", category: "Bid status" };
    case NotificationType.NEW_MESSAGE:
    case NotificationType.BID_MESSAGE:
      return { Icon: MessageSquare, tone: "info", category: "Message" };
    case NotificationType.CONNECTION_REQUEST:
      return { Icon: UserPlus, tone: "info", category: "Connection" };
    case NotificationType.CONNECTION_ACCEPTED:
      return { Icon: Handshake, tone: "success", category: "Connection" };
    case NotificationType.LISTING_TAG:
      return { Icon: Tag, tone: "violet", category: "Tagged" };
    case NotificationType.LISTING_BACK_ON_MARKET:
      return { Icon: RefreshCcw, tone: "brand", category: "Back on market" };
    case NotificationType.ENTITY_VERIFIED:
      return { Icon: ShieldCheck, tone: "success", category: "Verification" };
    case NotificationType.DOCUMENT_APPROVED:
      return { Icon: FileCheck2, tone: "success", category: "Verification" };
    case NotificationType.TIER_UPGRADED:
      return { Icon: Award, tone: "violet", category: "Verification" };
    case NotificationType.VERIFICATION_REJECTED:
      return { Icon: ShieldX, tone: "danger", category: "Verification" };
    case NotificationType.DOCUMENT_REJECTED:
      return { Icon: FileX2, tone: "danger", category: "Verification" };
    case "LISTING_EXPIRING_SOON":
    case "LISTING_EXPIRED":
    case "LISTING_OFFERS_REPORT":
      return { Icon: CalendarClock, tone: "warning", category: "Listing" };
    case "LISTING_PRICE_CHANGED":
      return { Icon: Banknote, tone: "info", category: "Listing" };
    // MKT-43 first look
    case "FIRST_LOOK_STARTED":
      return { Icon: Hourglass, tone: "violet", category: "First look" };
    case "FIRST_LOOK_OFFER":
      return { Icon: Banknote, tone: "success", category: "Instant offer" };
    case "FIRST_LOOK_RELEASED":
      return { Icon: Home, tone: "brand", category: "Listing" };
    // Epic 8
    case "INVITEE_OFFER_ACCEPTED":
      return { Icon: Handshake, tone: "success", category: "Your network" };
    case "AFFILIATE_ALERT":
      return { Icon: Briefcase, tone: "info", category: "Partner alert" };
    case "ESCROW_UPDATE":
      return { Icon: Handshake, tone: "info", category: "Escrow" };
    default:
      return { Icon: Bell, tone: "neutral", category: "Notification" };
  }
};

/** API data values can be numbers or numeric strings ("290000.00"); null when not a positive amount */
const toAmount = (value: unknown): number | null => {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

const toText = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/**
 * "New Property Match!" → "New property match". Keeps acronyms and codes (words with
 * a capital after the first letter, or digits) as they are.
 */
const CONNECTORS = new Set(["a", "an", "and", "at", "by", "for", "in", "of", "on", "or", "the", "to", "with"]);

export const toSentenceCase = (title: string): string => {
  const words = title.trim().replace(/!+$/, "").trim().split(/\s+/);
  // Already sentence case ("New match in Austin"): keep it, so proper nouns survive
  const isTitleCase = words.slice(1).every((w) => !/^[a-z]/.test(w) || CONNECTORS.has(w));
  if (!isTitleCase) return words.join(" ");
  return words
    .map((word, i) => {
      if (i === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (/[A-Z]/.test(word.slice(1)) || /\d/.test(word)) return word;
      return word.toLowerCase();
    })
    .join(" ");
};

/**
 * Sentence-case title that leads with the deal fact when the payload has it
 * ("Seller countered at $350,500"), otherwise the API title in sentence case.
 */
export const getNotificationTitle = (
  notification: Pick<NotificationDto, "type" | "title" | "data"> & { message?: string }
): string => {
  const data = notification.data ?? {};
  switch (notification.type) {
    case NotificationType.NEW_BID: {
      // Lead with the property; the amount and bidder go in the body so nothing repeats
      const street = getNotificationStreet(notification);
      if (street) return street;
      const buyer = toText(data.buyerName);
      if (buyer) return `New bid from ${buyer}`;
      const amount = toAmount(data.bidAmount);
      if (amount) return `New bid of ${formatCurrency(amount)}`;
      break;
    }
    case NotificationType.BID_COUNTERED: {
      const amount = toAmount(data.counterAmount);
      if (amount) return `Seller countered at ${formatCurrency(amount)}`;
      break;
    }
    case NotificationType.BID_ACCEPTED: {
      const amount = toAmount(data.bidAmount);
      if (amount) return `Your ${formatCurrency(amount)} bid was accepted`;
      break;
    }
    case NotificationType.BID_WON: {
      const amount = toAmount(data.bidAmount);
      if (amount) return `Your ${formatCurrency(amount)} offer won`;
      break;
    }
    case NotificationType.BID_MESSAGE:
    case NotificationType.NEW_MESSAGE: {
      const sender = toText(data.senderName);
      if (sender) return `Message from ${sender}`;
      break;
    }
    case NotificationType.BUY_BOX_MATCH: {
      const buyBox = toText(data.buyBoxName);
      if (buyBox) return `New match for ${buyBox}`;
      break;
    }
  }
  return toSentenceCase(notification.title || "Notification");
};

/** Types whose API message ends with "... on <address>" */
const ADDRESS_IN_MESSAGE = new Set<string>([NotificationType.NEW_BID, NotificationType.BID_COUNTERED]);

/**
 * The full listing address a notification is about: `data.listingAddress` (or `data.street`),
 * else the address the API message ends with for bid notifications. Null when unknown.
 */
export const getNotificationAddress = (
  notification: Pick<NotificationDto, "type" | "data"> & { message?: string }
): string | null => {
  const data = notification.data ?? {};
  const fromData = toText(data.listingAddress) ?? toText(data.street);
  if (fromData) return fromData;
  if (ADDRESS_IN_MESSAGE.has(notification.type) && notification.message) {
    const match = /\bon (\d[^.]*?)\.?$/.exec(notification.message.trim());
    if (match) return match[1].trim();
  }
  return null;
};

/** "6626 E Southern Ave, Mesa, AZ" -> "6626 E Southern Ave" */
export const getNotificationStreet = (
  notification: Pick<NotificationDto, "type" | "data"> & { message?: string }
): string | null => {
  const address = getNotificationAddress(notification);
  return address ? address.split(",")[0].trim() || null : null;
};

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/** "3 bd, 2 ba, 1,500 sq ft" from whatever facts the payload carries; null when none */
const listingFacts = (data: Record<string, unknown>): string | null => {
  const beds = toAmount(data.beds ?? data.bedsCount ?? data.bedrooms);
  const baths = toAmount(data.baths ?? data.bathsCount ?? data.bathrooms);
  const sqft = toAmount(data.sqft ?? data.squareFeet);
  const parts = [
    beds !== null ? `${beds} bd` : null,
    baths !== null ? `${baths} ba` : null,
    sqft !== null ? `${Math.round(sqft).toLocaleString("en-US")} sq ft` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

/**
 * One-line body built from the payload so it adds to the title instead of repeating it.
 * Falls back to the API message whenever the payload lacks the facts.
 */
export const getNotificationBody = (
  notification: Pick<NotificationDto, "type" | "title" | "data" | "message">
): string => {
  const data = notification.data ?? {};
  const message = notification.message ?? "";
  const type = notification.type as string;
  switch (type) {
    case NotificationType.NEW_BID: {
      const amount = toAmount(data.bidAmount);
      if (!amount || !getNotificationStreet(notification)) break;
      const buyer = toText(data.buyerName) ?? "A buyer";
      const asking = toAmount(data.askingPrice ?? data.listingPrice);
      let spread = "";
      if (asking !== null) {
        const diff = amount - asking;
        spread =
          diff === 0
            ? ", at asking"
            : `, ${formatCurrency(Math.abs(diff))} ${diff < 0 ? "below" : "above"} asking`;
      }
      return `${buyer} bid ${formatCurrency(amount)}${spread}`;
    }
    case NotificationType.BID_COUNTERED: {
      const yours = toAmount(data.originalBidAmount ?? data.previousBidAmount);
      const counter = toAmount(data.counterAmount);
      if (!yours || (counter !== null && yours === counter)) break;
      const street = getNotificationStreet(notification);
      return street
        ? `Your bid on ${street} was ${formatCurrency(yours)}`
        : `Your bid was ${formatCurrency(yours)}`;
    }
    case NotificationType.BUY_BOX_MATCH: {
      const address = getNotificationAddress(notification);
      if (!address) break;
      // Drop the ZIP: "3760 Culebra Rd, San Antonio, TX 78207" -> "3760 Culebra Rd, San Antonio, TX"
      const short = address.replace(/\s+\d{5}(-\d{4})?$/, "");
      const facts = listingFacts(data);
      return facts ? `${short}. ${facts}` : short;
    }
    case NotificationType.LISTING_TAG: {
      const street = getNotificationStreet(notification);
      if (!street) break;
      return `${toText(data.taggerName) ?? "Someone"} tagged you on ${street}`;
    }
    case "LISTING_EXPIRING_SOON": {
      const street = getNotificationStreet(notification);
      if (street) return `Your listing at ${street} is about to expire.`;
      break;
    }
    case "LISTING_EXPIRED": {
      const street = getNotificationStreet(notification);
      if (street) return `Your listing at ${street} has expired.`;
      break;
    }
  }
  return message;
};

/** Title for several new bids on the same listing, shown as one row */
export const getBidGroupTitle = (count: number, street: string | null): string =>
  street ? `${plural(count, "new bid")} on ${street}` : plural(count, "new bid");

/** Asking price carried by a match notification, when the payload includes one */
export const getNotificationPrice = (notification: Pick<NotificationDto, "type" | "data">): number | null =>
  notification.type === NotificationType.BUY_BOX_MATCH || notification.type === NotificationType.LISTING_BACK_ON_MARKET
    ? toAmount(notification.data?.listingPrice ?? notification.data?.askingPrice)
    : null;
